//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { MediaFilterParamKey } from '@apis/content-library/contentLibraryApiModel';
import { InvisibleButton } from '@components/atoms/button/Button';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import HR from '@components/atoms/hr/HR';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyText, CaptionSecondaryText } from '@components/atoms/text/Text';
import { CheckboxSetFilter } from '@components/media-filters/CheckboxSetFilter';
import { ClearSelectionButton } from '@components/media-filters/ClearSelectionButton';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import {
  DEFAULT_HAS_PRODUCTS_FILTER_SECTION,
  DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION,
  SELECTED_PRODUCTS_LIMIT,
} from '@components/media-filters/mediaFilterConstants';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useProductDetailsQuery } from '@content-drawer/filters/labels/hooks/useProductsQuery';
import { AllProductsModal } from '@content-drawer/filters/products/AllProductsModal';
import { ProductsTypeaheadSearch } from '@content-drawer/filters/products/ProductTypeaheadSearch';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export interface ProductTagsFilterProps {
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  section: FilterSection<boolean> | undefined;
  sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>;
}

export const ProductTagsFilter = ({
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section,
  sections,
}: ProductTagsFilterProps) => {
  const { data: catalogs } = useProductCatalogsQuery();

  const hasCatalog = isDefined(catalogs) && catalogs.length > 0;

  const initialSection = section ?? DEFAULT_HAS_PRODUCTS_FILTER_SECTION;

  const subSection: FilterSection<string> =
    getSection(sections, 'productIds') ??
    DEFAULT_SELECTED_PRODUCTS_FILTER_SECTION;

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const productStyleIds = subSection.options
    .filter((option) => option.isSelected)
    .map((option) => option.value);

  const { data: selectedProducts, isPending } = useProductDetailsQuery({
    fetchAllVariants: true,
    productStyleIds,
    select: (allProductDetails) => {
      return productStyleIds.map((styleId) => {
        const matchingProduct = allProductDetails.productVariants.find(
          (product) => product.styleId === styleId,
        );

        return {
          externalId: matchingProduct?.externalId,
          id: styleId,
          image: matchingProduct?.imageUrl,
          name: matchingProduct?.title,
        };
      });
    },
  });

  const updateSubSection = (
    options: FilterOption<FilterValue>[],
    shouldUpdateParent: boolean = false,
  ) => {
    const updatedSections = new Map<
      MediaFilterParamKey,
      FilterSection<FilterValue>
    >();

    updatedSections.set(subSection.paramKey, {
      ...subSection,
      options,
    });

    if (shouldUpdateParent) {
      const parentOptions = initialSection.options.map((option) => ({
        ...option,
        isSelected: !!option.value,
      }));

      updatedSections.set(initialSection.paramKey, {
        ...initialSection,
        options: parentOptions,
      });
    }

    onChange(updatedSections);
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        rightComponent={
          <HelpIcon
            helpText={
              hasCatalog
                ? 'Filter content with or without product tags. To filter by a specific product tag, you must select a product from your Vibe product catalog.'
                : 'Filter content with or without product tags.'
            }
            placement='left'
          />
        }
        title={FilterSectionTitle[subSection.paramKey]}
      >
        <>
          <CheckboxSetFilter
            isDisabled={isDisabled}
            isSingleSelect={true}
            onChange={onChange}
            section={initialSection}
            subsection={subSection}
          />
          {hasCatalog && (
            <ProductsSubsection>
              <ProductsTypeaheadSearch
                openModal={() => setIsOpen(true)}
                searchTerm={searchTerm}
                selectedProductIds={
                  selectedProducts?.map((product) => product.id) ?? []
                }
                selectProduct={(selectedProduct) => {
                  if (isDefined(selectedProducts)) {
                    const selectedOption = subSection.options.find(
                      (option) => option.value === selectedProduct.value,
                    );

                    if (
                      isUndefined(selectedOption) &&
                      selectedProducts.length < SELECTED_PRODUCTS_LIMIT
                    ) {
                      updateSubSection(
                        [...subSection.options, selectedProduct],
                        true,
                      );
                    } else if (isDefined(selectedOption)) {
                      updateSubSection(
                        subSection.options.map((option) => {
                          if (option.value === selectedProduct.value) {
                            return {
                              ...option,
                              isSelected:
                                selectedProducts.length <
                                SELECTED_PRODUCTS_LIMIT
                                  ? !selectedOption.isSelected
                                  : false,
                            };
                          }
                          return option;
                        }),
                        true,
                      );
                    }
                  }
                }}
                setSearchTerm={setSearchTerm}
              />
              <ClearSelectionButton
                handleClearSection={updateSubSection}
                options={subSection.options}
                trackingId={
                  MediaLibraryTrackingIds.PRODUCTS_CLEAR_SELECTION_BUTTON
                }
              />
              {isPending ? (
                <CenteredSpinner />
              ) : (
                selectedProducts
                  ?.slice(0, SELECTED_PRODUCTS_LIMIT)
                  .map((product) => (
                    <ProductContainer key={product.id}>
                      <ProductContents>
                        <StyledImage
                          alt={product.name ?? 'Product Image'}
                          src={product.image}
                        />
                        <div>
                          <ProductName ellipsize={1} title={product.name}>
                            {product.name}
                          </ProductName>
                          <ProductId ellipsize={1} title={product.externalId}>
                            {product.externalId}
                          </ProductId>
                        </div>
                      </ProductContents>
                      <RemoveProductButton
                        aria-label='Remove product'
                        iconName='close'
                        onClick={() => {
                          updateSubSection(
                            subSection.options.map((option) => {
                              if (option.value === product.id) {
                                return {
                                  ...option,
                                  isSelected: false,
                                };
                              }
                              return option;
                            }),
                          );
                        }}
                        trackingId={
                          MediaLibraryTrackingIds.PRODUCTS_REMOVE_PRODUCT_BUTTON
                        }
                        type='button'
                      />
                    </ProductContainer>
                  ))
              )}
              {isOpen && (
                <AllProductsModal
                  initialSearchTerm={searchTerm}
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  options={subSection.options}
                  updateSelectedProducts={(selectedProducts) =>
                    updateSubSection(selectedProducts, true)
                  }
                />
              )}
            </ProductsSubsection>
          )}
        </>
      </CollapsibleSection>

      <HR />
    </>
  );
};

const ProductsSubsection = styled.div(
  ({ theme }) => `
    padding-left: ${theme.spacings[4]};
  `,
);

const StyledImage = styled(ImageWithErrorHandling)(
  ({ theme }) => `
    width: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    height: calc(${theme.spacings[5]} + ${theme.spacings[1]});
    object-fit: contain;
  `,
);

const ProductContainer = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
    justify-content: space-between;
    padding-bottom: ${theme.spacings[1]};
  `,
);

const ProductId = styled(CaptionSecondaryText)`
  word-break: break-all;
`;

const RemoveProductButton = styled(InvisibleButton)(
  ({ theme }) => `
    width: ${theme.spacings[3]};
    height: ${theme.spacings[3]};
  `,
);

const ProductContents = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
    align-items: center;
  `,
);

const ProductName = styled(BodyText)`
  word-break: break-all;
`;
